/**
 * Blockly Workspace Screenshot Utility
 * 
 * This module provides functionality to download the Blockly workspace as a PNG image.
 * It preserves CSS styles and SVG structures by using a "nuclear solution" approach
 * that copies computed styles directly into the cloned SVG.
 */

import { BlocklyError } from "./errors";
import * as Sentry from "@sentry/react";

/**
 * Helper function to convert an image URL to a Base64 data URL
 * Useful for embedding external images into the SVG
 * 
 * @param url - The URL of the image to convert
 * @returns Promise resolving to the Base64 data URL
 */
function toDataURL(url: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = "Anonymous";

		img.onload = function () {
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');

			if (!ctx) {
				reject(new Error('Could not get canvas context'));
				return;
			}

			ctx.drawImage(img, 0, 0);
			resolve(canvas.toDataURL('image/png'));
		};

		img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
		img.src = url;
	});
}

/**
 * Downloads the Blockly workspace as a PNG image
 * 
 * This function:
 * 1. Gets the Blockly workspace and calculates block boundaries
 * 2. Clones the SVG and copies all computed styles (CSS preservation)
 * 3. Removes UI elements (scrollbars, trash, flyout, etc.)
 * 4. Converts external images to Base64
 * 5. Renders to canvas and downloads as PNG
 * 
 * @param onLoadingChange - Optional callback for loading state changes
 * @throws Error if Blockly workspace is not available or has no blocks
 */
export async function downloadBlocklyScreenshot(
	onLoadingChange?: (loading: boolean) => void
): Promise<string> {
	// Notify loading started
	onLoadingChange?.(true);

	try {
		// 1. Get Blockly workspace reference
		if (typeof (window as any).Blockly === 'undefined') {
			throw new Error("Blockly 'workspace' variable not found!");
		}

		const workspace = (window as any).shadowWorkspace;

		if (!workspace) {
			throw new Error("Could not get Blockly workspace!");
		}

		// 2. Get blocks bounding box
		const metrics = workspace.getBlocksBoundingBox();

		if (!metrics || metrics.top === undefined || metrics.bottom === undefined) {
			throw new Error("No blocks in workspace");
		}

		const contentWidth = metrics.width !== undefined ? metrics.width : (metrics.right - metrics.left);
		const contentHeight = metrics.height !== undefined ? metrics.height : (metrics.bottom - metrics.top);
		const margin = 20;

		// 3. Clone the original SVG
		const originalSvg = workspace.getParentSvg() as SVGElement;
		const svgClone = originalSvg.cloneNode(true) as SVGElement;
		svgClone.style.backgroundColor = "white";

		// --- STYLE COPY (NUCLEAR SOLUTION) ---
		// This section ensures CSS-defined styles are embedded into the SVG
		const originalElements = originalSvg.querySelectorAll('*');
		const clonedElements = svgClone.querySelectorAll('*');
		const count = Math.min(originalElements.length, clonedElements.length);

		for (let i = 0; i < count; i++) {
			const origEl = originalElements[i] as HTMLElement;
			const cloneEl = clonedElements[i] as HTMLElement;
			const tagName = origEl.tagName.toLowerCase();

			if (tagName === 'rect' || tagName === 'path' || tagName === 'text' || tagName === 'tspan') {
				const computed = window.getComputedStyle(origEl);

				if (computed.fill) cloneEl.style.fill = computed.fill;
				if (computed.stroke) cloneEl.style.stroke = computed.stroke;
				if (computed.strokeWidth) cloneEl.style.strokeWidth = computed.strokeWidth;
				if (computed.fontFamily) cloneEl.style.fontFamily = computed.fontFamily;
				if (computed.fontSize) cloneEl.style.fontSize = computed.fontSize;
				if (computed.fontWeight) cloneEl.style.fontWeight = computed.fontWeight;
				if (computed.visibility) cloneEl.style.visibility = computed.visibility;
				if (computed.opacity) cloneEl.style.opacity = computed.opacity;

				// Special fixes for Blockly editable text areas
				if (cloneEl.closest('.blocklyEditableText') && (tagName === 'rect' || tagName === 'path')) {
					cloneEl.style.setProperty('fill', '#FFFFFF', 'important');
					cloneEl.setAttribute('fill', '#FFFFFF');
				}

				if (cloneEl.closest('.blocklyEditableText') && (tagName === 'text')) {
					cloneEl.style.setProperty('fill', '#000000', 'important');
					cloneEl.setAttribute('fill', '#000000');
				}
			}
		}

		// 4. Clean up clone (remove unnecessary UI elements)
		const canvasGroups = svgClone.querySelectorAll('.blocklyBlockCanvas, .blocklyBubbleCanvas');
		canvasGroups.forEach(group => {
			group.removeAttribute('transform');
		});

		const uiElements = svgClone.querySelectorAll(
			'.blocklyScrollbarVertical, .blocklyScrollbarHorizontal, .blocklyZoom, .blocklyTrash, .blocklyFlyout, .blocklyMainBackground'
		);
		uiElements.forEach(el => el.remove());

		// 5. Embed images as Base64 (if blocks contain images)
		const images = svgClone.querySelectorAll('image');
		for (const img of Array.from(images)) {
			const href = img.getAttribute('xlink:href') || img.getAttribute('href');
			if (href && href.startsWith('http')) {
				try {
					const base64 = await toDataURL(href);
					img.setAttribute('xlink:href', base64);
					img.setAttribute('href', base64);
				} catch (e) {
					Sentry.captureException(new BlocklyError("Failed to convert image", e as Error));
				}
			}
		}

		// 6. Set ViewBox
		const x = metrics.left - margin;
		const y = metrics.top - margin;
		const w = contentWidth + 2 * margin;
		const h = contentHeight + 2 * margin;

		svgClone.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
		svgClone.setAttribute('width', w.toString());
		svgClone.setAttribute('height', h.toString());

		// 7. Draw to Canvas (SVG -> Canvas -> PNG)
		const serializer = new XMLSerializer();
		const svgStr = serializer.serializeToString(svgClone);
		const svgData = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgStr)));

		const canvas = document.createElement("canvas");
		canvas.width = w;
		canvas.height = h;
		const ctx = canvas.getContext("2d");

		if (!ctx) {
			throw new Error("Could not get canvas context");
		}

		// Create image and draw, return base64 PNG string
		const base64Png = await new Promise<string>((resolve, reject) => {
			const imgObj = new Image();

			imgObj.onload = function () {
				ctx.fillStyle = "#FFFFFF";
				ctx.fillRect(0, 0, w, h);
				ctx.drawImage(imgObj, 0, 0);

				resolve(canvas.toDataURL("image/png"));
			};

			imgObj.onerror = function (e) {
				console.error("SVG could not be loaded", e);
				reject(new Error("Failed to render SVG to image"));
			};

			imgObj.src = svgData;
		});

		return base64Png;

	} finally {
		// Always notify loading finished
		onLoadingChange?.(false);
	}

}
